import { useMutation, useQuery } from "@apollo/client";
import { CURRENT_USER, UPDATE_NOTIFICATIONS } from "@repo-sapiens/graphql";
import {
  PageLayout,
  Separator,
  ServiceItem,
  ServiceOptionsItem,
  Text,
  ToastMessage,
} from "../../../components";
import { useSubscriptionPlan } from "../../../hooks";
import { parseInt } from "lodash";
import React, { useEffect, useState } from "react";
import { GetMeResponse } from "../../../types";
import { NotificationStyle } from "../styles";

const meetingReminderOptions = [
  {
    label: "15m before",
    value: "15",
  },
  {
    label: "30m before",
    value: "30",
  },
  {
    label: "1h before",
    value: "60",
  },
];

type NotificationSettings = {
  hasNotification: boolean;
  morningSummary: boolean;
  notSendIfNoEvents: boolean;
  endOfDayNotification: boolean;
  endOfWeekNotification: boolean;
  sundayNotification: boolean;
  timeToReceivePrep: number;
};

export const NotificationsSreen: React.FC = () => {
  const [allDisabled, setAllDisabled] = useState(false);

  const {
    data: userData,
    refetch: refetchUser,
    loading,
  } = useQuery<GetMeResponse>(CURRENT_USER, {
    fetchPolicy: "network-only",
  });

  const { isFree, vipUser } = useSubscriptionPlan();

  const [settings, setSettings] = useState<NotificationSettings>({
    hasNotification: true,
    morningSummary: true,
    notSendIfNoEvents: false,
    endOfDayNotification: true,
    endOfWeekNotification: true,
    sundayNotification: true,
    timeToReceivePrep: 15,
  });

  useEffect(() => {
    if (userData?.me?.notifications && !loading) {
      setSettings({
        hasNotification: userData?.me?.notifications.hasNotification,
        morningSummary: userData?.me?.notifications.morningSummary,
        notSendIfNoEvents: userData?.me?.notifications.notSendIfNoEvents,
        endOfDayNotification: userData?.me?.notifications.endOfDayNotification,
        endOfWeekNotification:
          userData?.me?.notifications.endOfWeekNotification,
        sundayNotification: userData?.me?.notifications.sundayNotification,
        timeToReceivePrep: userData?.me?.notifications.timeToReceivePrep,
      });
    }
  }, [userData?.me?.notifications, loading]);

  const [showToast, setShowToast] = useState(false);

  const [update] = useMutation(UPDATE_NOTIFICATIONS);

  const submitChanges = async (
    key: keyof NotificationSettings,
    value: boolean | number,
  ) => {
    update({
      variables: {
        data: {
          ...settings,
          [key]: value,
        },
      },
    }).then(() => {
      setShowToast(true);
      refetchUser();
    });
  };

  const locked = isFree && !vipUser;
  const itemDisabled = allDisabled || locked;

  const handleNotification = (
    key: keyof NotificationSettings,
    value: boolean | string,
  ) => {
    if (itemDisabled) {
      return;
    }

    if (key === "hasNotification") {
      setAllDisabled(!value);
    }
    if (key === "timeToReceivePrep") {
      setSettings({
        ...settings,
        timeToReceivePrep: parseInt(value as string),
      });
      submitChanges("timeToReceivePrep", parseInt(value as string));
    } else {
      setSettings({ ...settings, [key]: value });
      submitChanges(key, value as boolean);
    }
  };

  return (
    <PageLayout title="Notifications | Multiplayer">
      <div className={NotificationStyle}>
        <ToastMessage
          show={showToast}
          handleShow={setShowToast}
          success
          duration={3000}
        />
        <Text.H2>Notifications</Text.H2>
        <Separator.Horizontal spacing={5} />

        <ServiceItem
          title="Enable Notifications"
          onSelected={(val) => handleNotification("hasNotification", val)}
          selected={settings.hasNotification}
          Icons={[]}
        />
        <ServiceItem
          title="Enable Morning Briefing Notifications"
          subtitle="Get a daily morning briefing email with a summary of your day."
          onSelected={(val) => handleNotification("morningSummary", val)}
          selected={itemDisabled ? false : settings.morningSummary}
          disabled={allDisabled || locked}
          Icons={[]}
        />
        <ServiceItem
          title="Enable End of Day Recap Notifications"
          subtitle="Get a daily end of day recap email with a summary of your day."
          onSelected={(val) => handleNotification("endOfDayNotification", val)}
          selected={itemDisabled ? false : settings.endOfDayNotification}
          disabled={allDisabled || locked}
          Icons={[]}
        />
        <ServiceItem
          title="Enable End of Week Recap Notifications"
          subtitle="Get a recap email of your week every Friday."
          onSelected={(val) => handleNotification("endOfWeekNotification", val)}
          selected={itemDisabled ? false : settings.endOfWeekNotification}
          disabled={allDisabled}
          Icons={[]}
        />
        <ServiceItem
          title="Enable Sunday Notifications"
          subtitle="Quickly refresh your memory on what's coming up next week."
          onSelected={(val) => handleNotification("sundayNotification", val)}
          selected={itemDisabled ? false : settings.sundayNotification}
          disabled={allDisabled || locked}
          Icons={[]}
        />
        <ServiceItem
          title="No empty Notifications"
          subtitle="Don't send me notifications if there's nothing to show."
          onSelected={(val) => handleNotification("notSendIfNoEvents", val)}
          selected={itemDisabled ? false : settings.notSendIfNoEvents}
          disabled={allDisabled}
          Icons={[]}
        />
        <ServiceOptionsItem
          title="Time to receive Meeting Prep"
          subtitle="A quick overview email for your upcoming external meeting with details on the attendees will be sent to you right before the meeting starts."
          onSelect={(val) => handleNotification("timeToReceivePrep", val)}
          Icons={[]}
          options={meetingReminderOptions}
          disabled={allDisabled || locked}
          value={settings.timeToReceivePrep.toString()}
        />
      </div>
    </PageLayout>
  );
};
